import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
  ValueEditorSwitch,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractFilterField } from '@/generated/gql/graphql';
import { ContractQueryFieldGroup } from '~/common/types';
import {
  listOperators,
  operators,
  queryFieldFactory,
  renderPercentageEditor,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';

const NAMESPACE_MAP = {
  [ContractFilterField.commodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [ContractFilterField.primaryCommodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [ContractFilterField.contractProductId]: CuriosityNamespaceType.COMMODITY,
  [ContractFilterField.contractServiceId]: CuriosityNamespaceType.COMMODITY,
  [ContractFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
  [ContractFilterField.supplierId]: CuriosityNamespaceType.SUPPLIER,
  [ContractFilterField.supplierIndustryId]: CuriosityNamespaceType.STD_NACE_CLASS,
};

export const useContractDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'Base.contract' });
    return {
      fields: [
        // ---------- CONTRACT INFO ----------
        createField({
          name: ContractFilterField.externalId,
          grp: ContractQueryFieldGroup.contract,
          operators: operators.text,
        }),
        createField({
          name: ContractFilterField.contractCreationDate,
          grp: ContractQueryFieldGroup.contract,
          operators: operators.date,
        }),
        createField({
          name: ContractFilterField.contractReceivedProposalsCount,
          grp: ContractQueryFieldGroup.contract,
          operators: operators.num,
        }),
        createField({
          name: ContractFilterField.contractAverageValueContribution,
          grp: ContractQueryFieldGroup.contract,
          operators: operators.num,
        }),
        createField({
          name: ContractFilterField.contractActive,
          grp: ContractQueryFieldGroup.contract,
          operators: operators.single,
        }),

        // ---------- SUPPLIER ----------
        createField({
          name: ContractFilterField.supplierId,
          grp: ContractQueryFieldGroup.supplier,
          operators: operators.id,
        }),
        createField({
          name: ContractFilterField.supplierExternalId,
          grp: ContractQueryFieldGroup.supplier,
          operators: operators.text,
        }),
        createField({
          name: ContractFilterField.supplierEmployeeCount,
          grp: ContractQueryFieldGroup.supplier,
          operators: operators.num,
        }),
        createField({
          name: ContractFilterField.supplierIndustryId,
          grp: ContractQueryFieldGroup.supplier,
          operators: operators.id,
        }),

        // ---------- PRODUCTS ----------
        createField({
          name: ContractFilterField.contractProductId,
          grp: ContractQueryFieldGroup.product,
          operators: operators.id,
        }),

        // ---------- SERVICES ----------
        createField({
          name: ContractFilterField.contractServiceId,
          grp: ContractQueryFieldGroup.service,
          operators: operators.text,
        }),
        // ---------- ORDERS ----------
        createField({
          name: ContractFilterField.orderLastDate,
          grp: ContractQueryFieldGroup.order,
          operators: operators.date,
        }),
        createField({
          name: ContractFilterField.orderVolumeYear,
          grp: ContractQueryFieldGroup.order,
          operators: operators.num,
        }),
        createField({
          name: ContractFilterField.orderCountYear,
          grp: ContractQueryFieldGroup.order,
          operators: operators.num,
        }),
        // ---------- COMMODITY GROUP ----------
        createField({
          name: ContractFilterField.primaryCommodityGroupId,
          grp: ContractQueryFieldGroup.commodityGroup,
          operators: operators.id,
        }),
        createField({
          name: ContractFilterField.commodityGroupId,
          grp: ContractQueryFieldGroup.commodityGroup,
          operators: operators.id,
        }),
        createField({
          name: ContractFilterField.commodityGroupCount,
          grp: ContractQueryFieldGroup.commodityGroup,
          operators: operators.num,
        }),
        // ---------- SOURCE SYSTEM ----------
        createField({
          name: ContractFilterField.sourceSystemId,
          grp: ContractQueryFieldGroup.sourceSystem,
          operators: operators.id,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case ContractFilterField.contractCreationDate:
          case ContractFilterField.orderLastDate:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      getDefaultValue: ({ field }) => {
        switch (field) {
          case ContractFilterField.contractActive:
            return false;
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          case ContractFilterField.primaryCommodityGroupId:
          case ContractFilterField.commodityGroupId:
          case ContractFilterField.sourceSystemId:
          case ContractFilterField.supplierIndustryId:
          case ContractFilterField.contractProductId:
          case ContractFilterField.supplierId:
            return (
              <CuriosityAutocomplete
                ns={NAMESPACE_MAP[field]}
                multiple={listOperators[operator]}
                simplifyValue
                {...otherProps}
              />
            );

          case ContractFilterField.supplierExternalId:
          case ContractFilterField.externalId:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          //
          case ContractFilterField.contractAverageValueContribution:
            return renderPercentageEditor({ operator, otherProps });
          //
          case ContractFilterField.contractReceivedProposalsCount:
          case ContractFilterField.supplierEmployeeCount:
          case ContractFilterField.orderVolumeYear:
          case ContractFilterField.orderCountYear:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          //
          case ContractFilterField.contractCreationDate:
          case ContractFilterField.orderLastDate:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          case ContractFilterField.contractActive:
            return <ValueEditorSwitch {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
